import { TokenTheme } from "../../../model/themes/SDKTokenTheme";
import { TokenComparator } from "../../../model/tokens/SDKTokenCompator";
import { replaceOriginReferencePersistentIdByIds, replaceReferencedTokenByIds, } from "../../../model/tokens/SDKTokenValue";
import { TokenUtils } from "../../../utils/TokenUtils";
import { DTPluginPreciseCopyStrategy, DTPluginThemeOverrideStrategy, isPreciseCopyEnabled, } from "./SDKDTMapLoader";
import flatMap from "lodash/flatMap";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Utility allowing merging of two pools of tokens */
export class DTThemeMerger {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId) {
        this.versionId = versionId;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Merger
    makeTheme(upstreamTokens, upstreamGroups, upstreamTheme, processedNodes, preciseCopy = DTPluginPreciseCopyStrategy.none, themeOverridesStrategy = DTPluginThemeOverrideStrategy.default) {
        var _a, _b;
        const isPreciseCopy = isPreciseCopyEnabled(preciseCopy);
        // Build map of existing tokens using path keys
        const existingOverrides = new Map();
        upstreamTheme.overriddenTokens.forEach((token) => {
            const path = TokenUtils.buildPath(token, upstreamGroups);
            const key = TokenUtils.buildKey(path, token.name);
            existingOverrides.set(key, token);
        });
        const newTokenSet = new Map();
        processedNodes.forEach((token) => {
            const key = TokenUtils.buildKey(token.path, token.token.name);
            newTokenSet.set(key, token);
        });
        const tokensInThemeButNotInUpstream = new Map(newTokenSet);
        const idsToRemap = new Map();
        // Create theme replica to fill with tokens
        const themeReplica = new TokenTheme({
            persistentId: upstreamTheme.id,
            id: upstreamTheme.idInVersion,
            brandId: upstreamTheme.brandId,
            meta: {
                name: upstreamTheme.name,
                description: upstreamTheme.description,
            },
            createdAt: upstreamTheme.createdAt
                ? upstreamTheme.createdAt.toISOString()
                : undefined,
            updatedAt: upstreamTheme.updatedAt
                ? upstreamTheme.updatedAt.toISOString()
                : undefined,
            codeName: upstreamTheme.codeName,
            overrides: [],
            designSystemVersionId: upstreamTheme.designSystemVersionId,
            parentPersistentId: (_a = upstreamTheme.parentPersistentId) !== null && _a !== void 0 ? _a : undefined,
            collectionPersistentIds: (_b = upstreamTheme.collectionPersistentIds) !== null && _b !== void 0 ? _b : [],
        }, this.versionId, upstreamTheme.collectionName);
        // Resolve each existing token that belong to the theme
        upstreamTokens.forEach((token) => {
            const path = TokenUtils.buildPath(token, upstreamGroups);
            const node = { token: token, path };
            const keys = TokenUtils.getKeysByKeyOrFallbackKeys(node);
            const incomingThemeOverride = TokenUtils.getTokenByKeyOrFallbackKeys(node, newTokenSet);
            const currentThemeOverride = TokenUtils.getTokenByKeyOrFallbackKeys(node, existingOverrides);
            keys.forEach((key) => {
                tokensInThemeButNotInUpstream.delete(key);
            });
            if (incomingThemeOverride) {
                if (incomingThemeOverride.token.id !== token.id) {
                    idsToRemap.set(incomingThemeOverride.token.id, token.id);
                    TokenUtils.replaceIdAcrossAllPossibleReferences(incomingThemeOverride, token.id, processedNodes);
                }
                // console.log(`overriding token from FT for key ${key}`)
                const incomingThemeDiffersFromBase = !TokenComparator.isEqualTokenValue(token, incomingThemeOverride.token);
                const { keepOverrideDueToAlias, incomingThemeDiffersFromUpstreamThemeAlias, } = this.applyThemeOverrideStrategy(idsToRemap, themeOverridesStrategy, token, incomingThemeOverride.token, currentThemeOverride);
                const incomingThemeDiffersFromUpstreamTheme = currentThemeOverride &&
                    !TokenComparator.isEqualTokenValue(currentThemeOverride, incomingThemeOverride.token);
                // For any defined override, use the new token, and align its id with the original token, if the token has different value
                if (incomingThemeDiffersFromBase || keepOverrideDueToAlias) {
                    // console.log(`value is not the same for token ${key}, using override`)
                    incomingThemeOverride.token.id = token.id;
                    themeReplica.overriddenTokens.push(incomingThemeOverride.token);
                }
                else if (incomingThemeDiffersFromUpstreamTheme ||
                    incomingThemeDiffersFromUpstreamThemeAlias) {
                    // base same as incoming theme, but differs from existing theme => we should remove override completely
                }
                else if (currentThemeOverride) {
                    themeReplica.overriddenTokens.push(currentThemeOverride);
                }
            }
            else if (currentThemeOverride && !isPreciseCopy) {
                // Otherwise use override that already exists without modifications
                // Unless preciseCopy = true and we need to remove currentThemeOverride
                themeReplica.overriddenTokens.push(currentThemeOverride);
            }
        });
        // Later when we build token that references `node.token`, we got updated id, as node.token was a reference.
        // Now we don't keep it as ref, so need to update id in all tokens that reference it.
        idsToRemap.forEach((newId, oldId) => {
            const tokensToIterate = [
                ...themeReplica.overriddenTokens,
                ...[...tokensInThemeButNotInUpstream.values()].map((t) => t.token),
            ];
            tokensToIterate.forEach((node) => {
                replaceReferencedTokenByIds(node, oldId, newId);
                replaceOriginReferencePersistentIdByIds(node, oldId, newId);
                // TODO: Fix inside Typography.fontSize
                TokenUtils.replaceIdForComplexTokens(node, oldId, newId);
            });
        });
        // Process tokens that were not in upstream, but are referenced in this theme
        this.inlineTokens(themeReplica, tokensInThemeButNotInUpstream);
        return themeReplica;
    }
    inlineTokens(themeReplica, tokensInThemeButNotInUpstream) {
        const idsCandidates = [...tokensInThemeButNotInUpstream.values()];
        const plainOverrides = themeReplica.overriddenTokens.filter((t) => !Array.isArray(t.value));
        const multiOverrides = themeReplica.overriddenTokens.filter((t) => Array.isArray(t.value));
        const themePlainOverrideRefs = flatMap(plainOverrides, (t) => {
            var _a;
            return [
                (_a = t.value) === null || _a === void 0 ? void 0 : _a.referencedTokenId,
                ...TokenUtils.getRefIdsFromComplexTokens(t),
            ];
        }).filter(Boolean);
        const themeMultiOverrideRefs = flatMap(multiOverrides, (t) => {
            var _a, _b;
            return [
                ...((_b = (_a = t.value) === null || _a === void 0 ? void 0 : _a.map((tv) => tv === null || tv === void 0 ? void 0 : tv.referencedTokenId)) !== null && _b !== void 0 ? _b : []),
                ...TokenUtils.getRefIdsFromComplexTokens(t),
            ];
        }).filter(Boolean);
        const allThemeOverrideRefs = [
            ...themePlainOverrideRefs,
            ...themeMultiOverrideRefs,
        ];
        const candidates = idsCandidates.filter((c) => allThemeOverrideRefs.includes(c.token.id));
        candidates.forEach((candidate) => {
            themeReplica.overriddenTokens.forEach((override) => {
                const token = override;
                if (!token) {
                    return;
                }
                if (Array.isArray(token.value)) {
                    // TODO: Handle this use case
                    // for (let i = 0; i < token.value.length; i++) {
                    //   if (token.value[i].referencedTokenId === candidate.token.id) {
                    //     token.value[i] = (candidate.token as AnyToken).value
                    //   }
                    // }
                }
                else if (token.value.referencedTokenId === candidate.token.id) {
                    // Do we need to copy something else apart from value?
                    token.value = { ...candidate.token.value };
                }
                TokenUtils.inlineValueIntoComplexTokens(token, candidate.token.id, {
                    ...candidate.token.value,
                });
            });
        });
    }
    // Decides whether or not we should add override for a particular token based on strategy, token, nodes & current theme
    applyThemeOverrideStrategy(idsToRemap, themeOverridesStrategy, token, incomingThemeOverrideToken, currentThemeOverride) {
        var _a, _b, _c;
        const isDefaultOverrideStrategy = themeOverridesStrategy === DTPluginThemeOverrideStrategy.default;
        // TODO: Handle multilayered tokens too
        const incomingThemeReferencedTokenId = (_a = incomingThemeOverrideToken === null || incomingThemeOverrideToken === void 0 ? void 0 : incomingThemeOverrideToken.value) === null || _a === void 0 ? void 0 : _a.referencedTokenId;
        const remappedId = idsToRemap.get(incomingThemeReferencedTokenId !== null && incomingThemeReferencedTokenId !== void 0 ? incomingThemeReferencedTokenId : "");
        const baseReferencedTokenId = (_b = token === null || token === void 0 ? void 0 : token.value) === null || _b === void 0 ? void 0 : _b.referencedTokenId;
        const incomingThemeRefTokenIdSameAsFromBase = baseReferencedTokenId === incomingThemeReferencedTokenId ||
            remappedId === baseReferencedTokenId;
        const keepOverrideDueToAlias = !isDefaultOverrideStrategy &&
            incomingThemeReferencedTokenId &&
            !incomingThemeRefTokenIdSameAsFromBase;
        const currentThemeReferencedTokenId = (_c = currentThemeOverride === null || currentThemeOverride === void 0 ? void 0 : currentThemeOverride.value) === null || _c === void 0 ? void 0 : _c.referencedTokenId;
        const incomingThemeRefTokenIdSameAsCurrentTheme = currentThemeReferencedTokenId === incomingThemeReferencedTokenId ||
            remappedId === currentThemeReferencedTokenId;
        const incomingThemeDiffersFromUpstreamThemeAlias = !isDefaultOverrideStrategy &&
            currentThemeReferencedTokenId &&
            !incomingThemeRefTokenIdSameAsCurrentTheme;
        return {
            keepOverrideDueToAlias,
            incomingThemeDiffersFromUpstreamThemeAlias,
        };
    }
}
